/**
 * My Active Work Component
 * 
 * Shows the agent's currently assigned book scan requests at the top
 * of the scan-agent portal. Enables seamless multi-device workflow:
 * - Scan on iPad → See "Continue editing on computer" prompt
 * - Open on laptop → See "Ready to edit" with one-click access
 * 
 * Features:
 * - Real-time updates via Supabase subscription
 * - Device-aware action recommendations
 * - Last activity timestamp with device indicator
 * - Quick-access buttons for scan/edit based on status
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import {
  BookOpen,
  Camera,
  Edit3,
  Clock,
  Smartphone,
  Tablet,
  Monitor,
  ChevronRight,
  Loader2,
  RefreshCw,
  Zap,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { glassStyles } from '@/lib/design';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase/client';
import {
  detectDeviceType,
  getDeviceDisplayName,
  getWorkflowRecommendation,
  type DeviceType,
} from '@/lib/device';

// ============================================================================
// Types
// ============================================================================

interface ActiveRequest {
  id: string;
  title: string;
  author: string | null;
  isbn: string | null;
  cover_image_url: string | null;
  status: 'assigned' | 'scanning' | 'editing';
  resolved_book_id: string | null;
  last_activity_at: string;
  last_device_type: DeviceType | null;
  assigned_at: string;
}

interface MyActiveWorkProps {
  onRefresh?: () => void;
}

// ============================================================================
// Component
// ============================================================================

export function MyActiveWork({ onRefresh }: MyActiveWorkProps) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  
  const [activeRequests, setActiveRequests] = useState<ActiveRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDevice] = useState<DeviceType>(detectDeviceType);
  const [releasingId, setReleasingId] = useState<string | null>(null);
  
  // ============================================================================
  // Data Fetching
  // ============================================================================
  
  const fetchActiveWork = useCallback(async () => {
    if (!profile?.id) return;
    
    try {
      const { data, error } = await supabase
        .from('book_scan_requests')
        .select('id, title, author, isbn, cover_image_url, status, resolved_book_id, last_activity_at, last_device_type, assigned_at')
        .eq('assigned_to', profile.id)
        .in('status', ['assigned', 'scanning', 'editing'])
        .is('deleted_at', null)
        .order('last_activity_at', { ascending: false });
      
      if (error) throw error;
      setActiveRequests((data || []) as ActiveRequest[]);
    } catch (err) {
      console.error('[MyActiveWork] Error fetching:', err);
      Sentry.captureException(err, { tags: { flow: 'scan_agent', step: 'fetch_active_work' } });
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);
  
  // Initial fetch
  useEffect(() => {
    fetchActiveWork();
  }, [fetchActiveWork]);
  
  // Real-time subscription for updates from other devices
  useEffect(() => {
    if (!profile?.id) return;
    
    const channel = supabase
      .channel('my-active-work')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'book_scan_requests',
          filter: `assigned_to=eq.${profile.id}`,
        },
        (payload) => {
          console.log('[MyActiveWork] Real-time update:', payload.eventType);
          fetchActiveWork();
          onRefresh?.();
        }
      )
      .subscribe();
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id, fetchActiveWork, onRefresh]);
  
  // ============================================================================
  // Actions
  // ============================================================================
  
  const updateDeviceType = async (requestId: string) => {
    await supabase
      .from('book_scan_requests')
      .update({ last_device_type: currentDevice })
      .eq('id', requestId);
  };
  
  // Release/unassign a request back to the queue
  const handleReleaseRequest = async (requestId: string) => {
    if (!profile?.id) return;
    
    setReleasingId(requestId);
    try {
      const { error } = await supabase
        .from('book_scan_requests')
        .update({ 
          assigned_to: null, 
          status: 'pending',
          last_activity_at: new Date().toISOString()
        })
        .eq('id', requestId)
        .eq('assigned_to', profile.id); // Security: only release your own
      
      if (error) throw error;
      
      // Remove from local state
      setActiveRequests(prev => prev.filter(r => r.id !== requestId));
      onRefresh?.();
    } catch (err) {
      console.error('[MyActiveWork] Error releasing request:', err);
      Sentry.captureException(err, { tags: { flow: 'scan_agent', step: 'release_request' } });
      toast.error('Failed to release request. Please try again.');
    } finally {
      setReleasingId(null);
    }
  };
  
  const handleScan = async (request: ActiveRequest) => {
    await updateDeviceType(request.id);
    navigate(`/scan-agent/scan/request/${request.id}`);
  };
  
  const handleEdit = async (request: ActiveRequest) => {
    if (!request.resolved_book_id) return;
    await updateDeviceType(request.id);
    navigate(`/scan-agent/edit/${request.resolved_book_id}`);
  };
  
  // ============================================================================
  // Helpers
  // ============================================================================
  
  const getDeviceIcon = (deviceType: DeviceType | null) => {
    switch (deviceType) {
      case 'mobile':
        return <Smartphone className="w-3 h-3" />;
      case 'tablet':
        return <Tablet className="w-3 h-3" />;
      case 'desktop':
        return <Monitor className="w-3 h-3" />;
      default:
        return null;
    }
  };
  
  const formatTimeAgo = (timestamp: string): string => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now.getTime() - then.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'assigned':
        return { bg: 'bg-[#0A84FF]/15', text: 'text-[#0A84FF]', label: 'Ready to Scan' };
      case 'scanning':
        return { bg: 'bg-[#5E5CE6]/15', text: 'text-[#5E5CE6]', label: 'Scanning' };
      case 'editing':
        return { bg: 'bg-[#30D158]/15', text: 'text-[#30D158]', label: 'Ready to Edit' };
      default:
        return { bg: 'bg-black/[0.05]', text: 'text-[#636366]', label: status };
    }
  };
  
  // ============================================================================
  // Render
  // ============================================================================
  
  // Don't show if no active work
  if (!loading && activeRequests.length === 0) {
    return null;
  }
  
  if (loading) {
    return (
      <div className="mb-6 p-4 rounded-2xl" style={glassStyles.card}>
        <div className="flex items-center gap-3">
          <Loader2 className="w-5 h-5 text-[#0A84FF] animate-spin" />
          <span className="text-[#8E8E93] text-sm">Loading your active work...</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="mb-6 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-[#FF9F0A]" />
          <span className="text-sm font-medium text-[#1C1C1E]">My Active Work</span>
          <span className="text-xs text-[#8E8E93]">
            ({activeRequests.length} in progress)
          </span>
        </div>
        <button
          onClick={() => fetchActiveWork()}
          className="p-1.5 rounded-lg hover:bg-black/[0.05] transition-colors text-[#8E8E93] hover:text-[#1C1C1E]"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      
      {/* Active Request Cards */}
      {activeRequests.map((request) => {
        const statusStyle = getStatusColor(request.status);
        const recommendation = getWorkflowRecommendation(
          currentDevice,
          request.status
        );
        
        return (
          <div
            key={request.id}
            className="p-4 rounded-2xl border border-[#FF9F0A]/20 bg-[#FF9F0A]/5"
          >
            <div className="flex items-start gap-4">
              {/* Cover */}
              <div className="w-14 h-18 bg-black/[0.03] rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                {request.cover_image_url ? (
                  <img
                    src={request.cover_image_url}
                    alt={request.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <BookOpen className="w-5 h-5 text-[#AEAEB2]" />
                )}
              </div>
              
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="font-medium text-[#1C1C1E] truncate">{request.title}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                    {statusStyle.label}
                  </span>
                </div>
                
                {request.author && (
                  <p className="text-sm text-[#8E8E93] mb-2 truncate">{request.author}</p>
                )}
                
                {/* Activity Info */}
                <div className="flex items-center gap-3 text-xs text-[#8E8E93] mb-3">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatTimeAgo(request.last_activity_at)}
                  </span>
                  {request.last_device_type && (
                    <span className="flex items-center gap-1">
                      {getDeviceIcon(request.last_device_type)}
                      {getDeviceDisplayName(request.last_device_type)}
                    </span>
                  )}
                </div>
                
                {/* Workflow Recommendation */}
                {recommendation.showDeviceHint && (
                  <p className="text-xs text-[#FF9F0A] mb-3">
                    💡 {recommendation.message}
                  </p>
                )}
                
                {/* Actions */}
                <div className="flex items-center gap-2">
                  {(request.status === 'assigned' || request.status === 'scanning') && (
                    <button
                      onClick={() => handleScan(request)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        recommendation.primaryAction === 'scan'
                          ? 'bg-[#5E5CE6] text-white hover:brightness-110'
                          : 'bg-black/[0.03] text-[#636366] hover:bg-black/[0.05]'
                      }`}
                    >
                      <Camera className="w-4 h-4" />
                      {request.status === 'scanning' ? 'Continue Scanning' : 'Start Scanning'}
                    </button>
                  )}
                  
                  {request.status === 'editing' && request.resolved_book_id && (
                    <button
                      onClick={() => handleEdit(request)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                        recommendation.primaryAction === 'edit'
                          ? 'bg-[#30D158] text-white hover:brightness-110'
                          : 'bg-black/[0.03] text-[#636366] hover:bg-black/[0.05]'
                      }`}
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit Content
                    </button>
                  )}
                  
                  <button
                    onClick={() => navigate(`/scan-agent`)}
                    className="flex items-center gap-1 px-2 py-1.5 text-xs text-[#8E8E93] hover:text-[#1C1C1E] transition-colors"
                  >
                    View in Queue
                    <ChevronRight className="w-3 h-3" />
                  </button>
                  
                  {/* Release back to queue */}
                  <button
                    onClick={() => handleReleaseRequest(request.id)}
                    disabled={releasingId === request.id}
                    className="flex items-center gap-1 px-2 py-1.5 text-xs text-[#8E8E93] hover:text-[#FF453A] transition-colors disabled:opacity-50"
                    title="Release back to queue"
                  >
                    {releasingId === request.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <XCircle className="w-3 h-3" />
                    )}
                    Release
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default MyActiveWork;
